import { useState, useEffect } from 'react'
import { surpriseBagService, type SurpriseBagStats } from '@/services/surpriseBagService'
import { Gift, TrendingUp, ShoppingBag, Percent, Star, Store, Calendar } from 'lucide-react'

export default function SurpriseBagAnalytics() {
  const [stats, setStats] = useState<SurpriseBagStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true)
        const data = await surpriseBagService.getSurpriseBagStats()
        setStats(data)
      } catch (err) {
        console.error('Error loading surprise bag stats:', err)
        setError('Impossible de charger les statistiques des sacs surprise.')
      } finally {
        setLoading(false)
      }
    }
    loadStats()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Gift className="h-8 w-8 text-red-500" />
        </div>
        <p className="text-gray-600">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytiques des sacs surprise</h1>
          <p className="text-sm text-gray-500 mt-1">
            Statistiques et tendances des offres sac surprise
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total sacs surprise</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats?.total_surprise_bags ?? 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Gift className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Actifs</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {stats?.active_surprise_bags ?? 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Révélations totales</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats?.total_reveals ?? 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Économie moyenne</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">
                {stats?.average_savings_percentage ?? 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Percent className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Most Common Contents */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Star className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Contenus les plus fréquents</h2>
          </div>
        </div>
        <div className="p-6">
          {stats?.most_common_contents && stats.most_common_contents.length > 0 ? (
            <div className="space-y-3">
              {stats.most_common_contents.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                    <span className="text-sm font-medium text-gray-900">{item.item}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-amber-500 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            (item.count / Math.max(...stats.most_common_contents.map((c) => c.count))) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-500 font-medium">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              Aucune donnée de contenu disponible
            </p>
          )}
        </div>
      </div>

      {/* By Merchant */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Store className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">Par marchand</h2>
          </div>
        </div>
        <div className="p-6">
          {stats?.by_merchant && stats.by_merchant.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marchand
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sacs créés
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Révélations
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.by_merchant.map((merchant, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {merchant.merchant_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{merchant.count}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{merchant.total_reveals}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              Aucune donnée par marchand disponible
            </p>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900">Tendance mensuelle</h2>
          </div>
        </div>
        <div className="p-6">
          {stats?.monthly_trend && stats.monthly_trend.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mois
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Créés
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Révélés
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.monthly_trend.map((month, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{month.month}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{month.created}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{month.revealed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              Aucune donnée de tendance disponible
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
